import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { postRequest } from "api/tools/httpRequests";
import {
  IDMCActivityWithTourOptionsAndRates,
  IDMCActivityWTourOptions,
} from "commonFnBcode/types/dmc.types";
import { IJobsheetDateRangeState } from "dmcMaster/jobSheet/DmcJobsheet";
import moment from "moment";
import { toast } from "react-toastify";
import { RootState } from "redux/store";

interface IDateRange {
  from: Date;
  to: Date;
}

export interface IActivitySearch {
  dateRange: IDateRange;
  selectedCity: number | undefined;

  passengers: {
    adults: number;
    children: number;
    infants: number;
  };
  activities?: IDMCActivityWithTourOptionsAndRates[];
}

const initialState: IActivitySearch = {
  dateRange: {
    from: new Date(),
    to: moment().add(4, "days").toDate(),
  },
  selectedCity: undefined,
  passengers: {
    adults: 1,
    children: 0,
    infants: 0,
  },
  activities: [],
};

// thunk

interface ISearchParams {
  dateRange: IJobsheetDateRangeState;
  selectedCity: number;
  search: string | null;
  passengers: {
    adults: number;
    children: number;
    infants: number;
  };
}

export const fetchActivitiesByCityId = createAsyncThunk<
  void,
  ISearchParams,
  {
    state: RootState;
  }
>("activities/searchActivities", async (props, thunkAPI) => {
  try {
    const response = await postRequest(
      `activities/searchActivitiesByCityId`,
      props
    );
    const reduxState = thunkAPI.getState();

    if (reduxState.activityCart.cartData?.id) {
      await postRequest(`activities/cart/updateCartData`, {
        cartId: reduxState.activityCart.cartData?.id,
        adults: props.passengers.adults,
        children: props.passengers.children,
        startDate: moment(props.dateRange.from).format("YYYY-MM-DD"),
        endDate: moment(props.dateRange.to).format("YYYY-MM-DD"),
      });
    }

    thunkAPI.dispatch(activitySearchActions.setSearchData(props));

    thunkAPI.dispatch(
      activitySearchActions.setSearchResults({
        activities: response.data.result.activities,
      })
    );

    return response;
  } catch (error) {
    toast.error("Error fetching activity search");
  }
});

export const ActivitySearchSlice = createSlice({
  name: "ActivitySearch",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setSearchData: (state, action: PayloadAction<ISearchParams>) => {
      state.dateRange = action.payload.dateRange;
      state.selectedCity = action.payload.selectedCity;
      state.passengers = action.payload.passengers;
    },
    setDateRange: (state, action: PayloadAction<IJobsheetDateRangeState>) => {
      state.dateRange = action.payload;
    },
    setPassengers: (
      state,
      action: PayloadAction<{
        adults: number;
        children: number;
        infants: number;
      }>
    ) => {
      state.passengers = action.payload;
    },
    setSelectedCity: (state, action: PayloadAction<number | undefined>) => {
      state.selectedCity = action.payload;
    },
    setSearchResults: (
      state,
      action: PayloadAction<{
        activities: IDMCActivityWithTourOptionsAndRates[];
      }>
    ) => {
      state.activities = action.payload.activities;
    },
  },
});

export const activitySearchActions = ActivitySearchSlice.actions;

export const selectActivitySearchSlice = (state: RootState) => {
  return state.searchFlight;
};

export const activitySearchReducer = ActivitySearchSlice.reducer;
